import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import {
  Phone, Navigation, ClipboardCheck, Clock, Star, Zap, UserCheck, Award, CheckCircle2,
  ChevronDown, MapPin, ArrowRight, Wrench, DollarSign, ShieldCheck, Gauge,
} from "lucide-react";
import { BUSINESS, mapsDirUrl, mapsSearchUrl, mapsEmbedUrl } from "../config/business";
import { SERVICES, FAQS, PROCESS } from "../config/content";
import { REVIEWS, REVIEW_META } from "../config/reviews";
import SEO from "../components/SEO";
import { FadeUp, MaskedLine } from "../components/anim";
import OpenNowBadge from "../components/OpenNowBadge";
import { fetchCoupons } from "../lib/api";

const trust = [
  { icon: Zap, title: "15–20 Min", sub: "Average Smog Test" },
  { icon: UserCheck, title: "Walk-Ins", sub: "Always Welcome" },
  { icon: Award, title: "STAR", sub: "Certified Station" },
  { icon: ClipboardCheck, title: "Instant", sub: "Electronic Results" },
];

const why = [
  { icon: Zap, title: "Fast Service", sub: "In & Out Quickly" },
  { icon: DollarSign, title: "Clear Pricing", sub: "No Surprise Upcharges" },
  { icon: UserCheck, title: "Walk-Ins Welcome", sub: "No Appointment Needed" },
  { icon: Wrench, title: "Experienced Techs", sub: "Certified & Honest" },
];

export default function Home() {
  const [coupon, setCoupon] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    fetchCoupons().then((d) => d?.length && setCoupon(d[0])).catch(() => {});
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid="home-page">
      <SEO
        title={`Smog Check Modesto, CA | STAR Station | ${BUSINESS.name}`}
        description={`Fast STAR smog check in Modesto, CA at ${BUSINESS.address.full}. Walk-ins welcome, most tests in 15–20 min. Diesel, STAR & repairs. Call ${BUSINESS.phoneDisplay}.`}
        keywords="Smog Check Modesto, STAR Smog Modesto, Smog Check Ceres, Diesel Smog Modesto, DMV registration Modesto"
        path="/"
        schema={[faqSchema]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-white aurora">
        <div className="section relative z-10 grid lg:grid-cols-2 gap-10 items-center py-14 lg:py-20">
          <div>
            <span className="chip" data-testid="hero-badge">
              <Star className="h-3.5 w-3.5 fill-star text-star" /> STAR Certified Smog Check Station
            </span>
            <h1 className="mt-5 font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              <MaskedLine
                lines={[
                  { text: "Fast & Reliable", className: "text-ink" },
                  { text: "Smog Check in Modesto, CA", className: "text-volt" },
                ]}
              />
            </h1>
            <p className="mt-5 text-base text-ink/70 font-body max-w-xl">
              Visit {BUSINESS.name} at {BUSINESS.address.full} for fast, professional smog testing, diesel checks and DMV registration — skip the lines and walk out with your tags.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-volt" data-testid="hero-call"><Phone className="h-4 w-4" /> Call Now</a>
              <a href={mapsDirUrl()} target="_blank" rel="noreferrer" className="btn-outline" data-testid="hero-directions"><Navigation className="h-4 w-4" /> Get Directions</a>
              <Link to="/contact" className="btn-cta" data-testid="hero-quote"><ClipboardCheck className="h-4 w-4" /> Get a Smog Check</Link>
            </div>
            <p className="mt-5 text-sm text-ink/60 font-body inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-volt" /> {BUSINESS.hoursSummary} · <OpenNowBadge className="text-sm" />
            </p>
          </div>

          <FadeUp className="relative">
            <div className="relative rounded-2xl overflow-hidden border border-line shadow-xl">
              <img
                src={BUSINESS.images.storefront}
                alt="Smog Hut Modesto STAR certified smog check station storefront"
                className="w-full h-[360px] lg:h-[440px] object-cover"
                fetchPriority="high"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="absolute -left-3 bottom-6 card-soft px-4 py-3 flex items-center gap-3"
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-volt-dim text-volt"><Zap className="h-5 w-5" /></span>
              <div><p className="font-head font-bold text-ink leading-none">15–20 Min</p><p className="text-xs text-ink/60">Average Smog Test</p></div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
              className="absolute -right-2 top-6 card-soft px-4 py-3 flex items-center gap-3"
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-cta/20 text-star"><Award className="h-5 w-5" /></span>
              <div><p className="font-head font-bold text-ink leading-none">STAR Certified</p><p className="text-xs text-ink/60">All DMV-Directed</p></div>
            </motion.div>
          </FadeUp>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-softblue">
        <div className="section py-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {trust.map((t, i) => (
            <FadeUp key={t.title} delay={i * 0.06}>
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-white text-volt shadow-sm"><t.icon className="h-5 w-5" /></span>
                <div><p className="font-head font-bold text-ink leading-none">{t.title}</p><p className="text-xs text-ink/60 mt-1">{t.sub}</p></div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="section py-16">
        <FadeUp className="max-w-2xl">
          <p className="kicker">Our Services</p>
          <h2 className="mt-2 font-head font-extrabold text-3xl md:text-4xl text-ink">Complete Smog & Registration Solutions</h2>
          <p className="mt-3 text-ink/70 font-body">Everything your vehicle needs to pass the California smog check — handled in one quick visit in Modesto.</p>
        </FadeUp>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.slice(0, 5).map((s, i) => (
            <FadeUp key={s.slug} delay={i * 0.05}>
              <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`} className="group block rounded-2xl bg-[#EAF2FE] border border-line p-6 h-full hover:shadow-volt transition-shadow">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-white text-volt shadow-sm"><Gauge className="h-6 w-6" /></span>
                <h3 className="mt-4 font-head font-bold text-lg text-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-ink/70 font-body">{s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-head font-semibold text-volt group-hover:gap-2 transition-all">Learn More <ArrowRight className="h-4 w-4" /></span>
              </Link>
            </FadeUp>
          ))}
          <FadeUp delay={0.25}>
            <Link to="/coupons" data-testid="home-service-coupons" className="group block rounded-2xl bg-volt text-white p-6 h-full hover:shadow-volt transition-shadow">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/15"><DollarSign className="h-6 w-6" /></span>
              <h3 className="mt-4 font-head font-bold text-lg">Coupons & Specials</h3>
              <p className="mt-2 text-sm text-white/80 font-body">Save with our latest smog check coupons and special offers.</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-head font-semibold group-hover:gap-2 transition-all">View Coupons <ArrowRight className="h-4 w-4" /></span>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-softblue">
        <div className="section py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <FadeUp className="max-w-xl">
              <p className="kicker">Why Choose {BUSINESS.name}</p>
              <h2 className="mt-2 font-head font-extrabold text-3xl md:text-4xl text-ink">Trusted by Modesto & Ceres Drivers</h2>
              <p className="mt-3 text-ink/70 font-body">Honest service, clear pricing and professional care — with a 90-day free retest for peace of mind.</p>
            </FadeUp>
            <a href={`tel:${BUSINESS.phoneTel}`} className="btn-volt shrink-0"><Phone className="h-4 w-4" /> {BUSINESS.phoneDisplay}</a>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {why.map((w, i) => (
              <FadeUp key={w.title} delay={i * 0.06}>
                <div className="card-soft p-6 h-full">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-volt-dim text-volt"><w.icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-head font-bold text-ink">{w.title}</h3>
                  <p className="mt-1 text-sm text-ink/60 font-body">{w.sub}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section py-16">
        <FadeUp className="text-center max-w-2xl mx-auto">
          <p className="kicker">How It Works</p>
          <h2 className="mt-2 font-head font-extrabold text-3xl md:text-4xl text-ink">Our Smog Check Process</h2>
        </FadeUp>
        <div className="mt-12 relative grid sm:grid-cols-3 lg:grid-cols-5 gap-8">
          <div className="hidden lg:block absolute top-7 left-[10%] right-[10%] border-t-2 border-dashed border-line" />
          {PROCESS.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.08} className="relative text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-volt text-white font-head font-extrabold text-lg shadow-volt relative z-10">{i + 1}</div>
              <h3 className="mt-4 font-head font-bold text-ink">{p.title}</h3>
              <p className="mt-1 text-sm text-ink/60 font-body">{p.text}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* COUPON */}
      <section className="section pb-16" id="coupon">
        <FadeUp>
          <div className="dashed-ticket rounded-2xl p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="kicker">Special Offer</p>
              <h2 className="mt-1 font-head font-extrabold text-4xl text-ink">{coupon?.title || "$20 OFF Smog Check"}</h2>
              <p className="mt-2 text-ink/70 font-body max-w-lg">{coupon?.description || "Mention this coupon and get $20 OFF any smog check at our Modesto station."}</p>
              <p className="mt-2 text-xs text-ink/50 font-body">{coupon?.terms || "Mention or show this coupon at time of service. Not valid with other offers."}</p>
            </div>
            <div className="flex flex-col gap-2 md:w-64">
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-cta w-full"><Phone className="h-4 w-4" /> Call to Redeem</a>
              <Link to="/coupons" className="btn-outline w-full">All Coupons</Link>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* REVIEWS */}
      <section className="bg-softblue">
        <div className="section py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <FadeUp>
              <p className="kicker">Customer Reviews</p>
              <h2 className="mt-2 font-head font-extrabold text-3xl md:text-4xl text-ink">What Our Customers Say</h2>
              <p className="mt-3 text-ink/70 font-body">Modesto and Ceres drivers rate {BUSINESS.name} for fast, friendly and honest smog service. Read real reviews — and share your own.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={BUSINESS.social.yelp} target="_blank" rel="noreferrer" className="btn-volt" data-testid="reviews-read"><Star className="h-4 w-4" /> Read All Reviews</a>
                <a href={BUSINESS.google.profileUrl} target="_blank" rel="noreferrer" className="btn-outline" data-testid="reviews-write"><GoogleG /> Write a Review</a>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="card-soft p-8 text-center">
                <div className="flex justify-center mb-2"><GoogleG large /></div>
                {BUSINESS.google.rating ? (
                  <>
                    <p className="font-head font-extrabold text-6xl text-ink">{BUSINESS.google.rating}</p>
                    <div className="mt-2 flex justify-center gap-1">
                      {[...Array(5)].map((_, i) => <Star key={i} className="h-6 w-6 fill-cta text-cta" />)}
                    </div>
                    <p className="mt-2 text-ink/60 font-body">Based on {BUSINESS.google.reviewCount} reviews</p>
                  </>
                ) : (
                  <>
                    <div className="mt-2 flex justify-center gap-1">
                      {[...Array(5)].map((_, i) => <Star key={i} className="h-7 w-7 fill-cta text-cta" />)}
                    </div>
                    <p className="mt-3 font-head font-bold text-xl text-ink">Loved by {REVIEW_META.count}+ {REVIEW_META.source} reviewers</p>
                    <p className="mt-1 text-sm text-ink/60 font-body">Read our verified customer reviews and leave your own.</p>
                  </>
                )}
                <a href={BUSINESS.social.yelp} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-head font-semibold text-volt hover:underline">
                  {BUSINESS.name} · Modesto, CA <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </FadeUp>
          </div>

          {/* Real review cards */}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REVIEWS.slice(0, 6).map((r, i) => (
              <FadeUp key={r.name + i} delay={i * 0.05}>
                <div className="card-soft p-6 h-full flex flex-col" data-testid={`review-card-${i}`}>
                  <div className="flex items-center gap-1">
                    {[...Array(r.rating)].map((_, s) => <Star key={s} className="h-4 w-4 fill-cta text-cta" />)}
                  </div>
                  <p className="mt-3 text-sm text-ink/75 font-body leading-relaxed flex-1">“{r.text}”</p>
                  <div className="mt-4 flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-volt text-white font-head font-bold text-sm">{r.name.charAt(0)}</span>
                    <div>
                      <p className="font-head font-bold text-sm text-ink leading-none">{r.name}</p>
                      <p className="text-xs text-ink/50 mt-0.5">{r.date}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section py-16">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <FadeUp>
            <p className="kicker">FAQ</p>
            <h2 className="mt-2 font-head font-extrabold text-3xl md:text-4xl text-ink">Frequently Asked Questions</h2>
            <p className="mt-3 text-ink/70 font-body">Everything Modesto drivers ask us about smog checks and STAR inspections.</p>
            <a href={`tel:${BUSINESS.phoneTel}`} className="btn-volt mt-6"><Phone className="h-4 w-4" /> Ask Us Directly</a>
          </FadeUp>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FadeUp key={i} delay={i * 0.03}>
                <div className="card-soft overflow-hidden" data-testid={`faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                    <span className="font-head font-semibold text-ink">{f.q}</span>
                    <ChevronDown className={`h-5 w-5 text-volt shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <p className="px-5 pb-5 text-sm text-ink/70 font-body">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT US */}
      <section className="bg-softblue">
        <div className="section py-16 grid lg:grid-cols-3 gap-8">
          <FadeUp>
            <h2 className="font-head font-extrabold text-3xl text-ink">Visit {BUSINESS.name}</h2>
            <ul className="mt-5 space-y-3 text-sm">
              <li><a href={mapsSearchUrl()} target="_blank" rel="noreferrer" className="inline-flex items-start gap-2 text-ink hover:text-volt"><MapPin className="h-5 w-5 text-volt" />{BUSINESS.address.full}</a></li>
              <li><a href={`tel:${BUSINESS.phoneTel}`} className="inline-flex items-center gap-2 text-ink hover:text-volt"><Phone className="h-5 w-5 text-volt" />{BUSINESS.phoneDisplay}</a></li>
            </ul>
            <div className="mt-4 text-sm text-ink/70 font-body">
              <p className="font-semibold text-ink">MON–FRI: 8:30 AM – 5 PM</p>
              <p className="font-semibold text-ink">SAT: 9 AM – 4 PM</p>
            </div>
            <a href={mapsDirUrl()} target="_blank" rel="noreferrer" className="btn-cta mt-5"><Navigation className="h-4 w-4" /> Get Directions</a>
            <p className="mt-5 text-sm text-ink/70 font-body">Areas we serve:{" "}
              {BUSINESS.cities.map((c, i) => (
                <span key={c.slug}><Link to={`/locations/${c.slug}`} className="text-volt hover:underline">{c.name}</Link>{i < BUSINESS.cities.length - 1 ? ", " : ""}</span>
              ))}
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-1">
            <div className="rounded-2xl overflow-hidden border border-line shadow-sm h-full min-h-[300px]">
              <iframe title="Map" src={mapsEmbedUrl()} className="w-full h-full min-h-[300px]" loading="lazy" />
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="card-soft p-6">
              <h3 className="font-head font-bold text-ink">Why Choose Us?</h3>
              <ul className="mt-4 space-y-2.5 text-sm">
                {["STAR Certified Smog Check Station", "Licensed & Insured Technicians", "Latest Testing Equipment", "Honest & Transparent Service", "Serving Modesto & Surrounding Areas"].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-ink/80"><CheckCircle2 className="h-5 w-5 text-openg shrink-0" />{t}</li>
                ))}
              </ul>
              <div className="mt-5 rounded-xl bg-volt-dim p-4 flex items-center gap-3">
                <ShieldCheck className="h-8 w-8 text-volt" />
                <div><p className="font-head font-bold text-ink leading-none">STAR Certified</p><p className="text-xs text-ink/60 mt-1">Smog Check Station</p></div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-volt">
        <div className="section py-14 text-center">
          <FadeUp>
            <h2 className="font-head font-extrabold text-3xl md:text-4xl text-white">Need a smog check today?</h2>
            <p className="mt-3 text-white/85 font-body">Walk in during business hours — most tests done in 15–20 minutes.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <a href={`tel:${BUSINESS.phoneTel}`} className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-head font-bold text-volt hover:scale-[1.03] transition-transform"><Phone className="h-4 w-4" /> {BUSINESS.phoneDisplay}</a>
              <a href={mapsDirUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 px-7 py-3.5 font-head font-bold text-white hover:bg-white/10 transition-colors"><Navigation className="h-4 w-4" /> Get Directions</a>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

function GoogleG({ large }) {
  const s = large ? 40 : 18;
  return (
    <svg width={s} height={s} viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
